import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiGet } from "../lib/api";
import { useParish } from "../lib/parishContext";
import type {
  PublicAssignment,
  PublicSchedulePayload,
} from "../types/schedule";
import ParishLogo from "../components/ParishLogo";
import PoweredByFooter from "../components/PoweredByFooter";
import { roleLabel } from "../lib/role-label";
import { payloadTimeZone, zonedDayKey, zonedMonthKey } from "../lib/timezone";

type State =
  | { kind: "loading" }
  | { kind: "ready"; data: PublicSchedulePayload }
  | { kind: "not-found" }
  | { kind: "error"; message: string };

export default function PublicSchedule() {
  const { parish: parishSlug, token } = useParams();
  const { parish } = useParish();
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    if (!parishSlug || !token) {
      setState({ kind: "not-found" });
      return;
    }
    let cancelled = false;
    apiGet<PublicSchedulePayload>(`/schedule/${parishSlug}/${token}`)
      .then((data) => {
        if (cancelled) return;
        setState({ kind: "ready", data });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        // The worker returns 404 for unknown/expired tokens. Anything
        // else we treat as a generic transient error.
        const msg = err.message ?? "";
        if (/\b404\b/.test(msg)) {
          setState({ kind: "not-found" });
        } else {
          setState({ kind: "error", message: msg });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [parishSlug, token]);

  useEffect(() => {
    const original = document.title;
    document.title = `Your schedule — ${parish.display_name}`;
    return () => {
      document.title = original;
    };
  }, [parish.display_name]);

  return (
    <main className="page page--public-schedule">
      <header className="parish-help__band">
        <ParishLogo size={48} />
      </header>

      <section className="parish-help__hero">
        <h1 className="parish-help__title">Your schedule</h1>
        <p className="parish-help__intro">
          Upcoming Masses
          {state.kind === "ready" && state.data.volunteer.firstName
            ? ` for ${state.data.volunteer.firstName}`
            : ""}
          {" at "}
          {parish.display_name}
        </p>
      </section>

      <div className="public-schedule__body">
        {state.kind === "loading" ? <SkeletonList /> : null}
        {state.kind === "not-found" ? (
          <Notice
            title="We couldn't find this schedule."
            body="The link may be invalid or expired. Contact your parish if you need a new one."
          />
        ) : null}
        {state.kind === "error" ? (
          <Notice
            title="Couldn't load your schedule right now."
            body="Try again in a minute."
            retry={() => {
              setState({ kind: "loading" });
              if (!parishSlug || !token) return;
              apiGet<PublicSchedulePayload>(`/schedule/${parishSlug}/${token}`)
                .then((data) => setState({ kind: "ready", data }))
                .catch((err: Error) =>
                  setState({ kind: "error", message: err.message }),
                );
            }}
          />
        ) : null}
        {state.kind === "ready" ? (
          <ScheduleBody data={state.data} parishName={parish.display_name} />
        ) : null}
      </div>

      <PoweredByFooter />
    </main>
  );
}

function ScheduleBody({
  data,
  parishName,
}: {
  data: PublicSchedulePayload;
  parishName: string;
}) {
  if (data.assignments.length === 0) {
    return (
      <Notice
        title="You're not scheduled for any upcoming Masses right now."
        body={`If that's a surprise, reach out to ${parishName}.`}
      />
    );
  }
  const timeZone = payloadTimeZone(data.timezone);
  const groups = groupByMonth(data.assignments, timeZone);
  return (
    <>
      {groups.map((g) => (
        <section
          key={g.key}
          className="public-schedule__group"
          aria-labelledby={`group-${g.key}`}
        >
          <h2 id={`group-${g.key}`} className="public-schedule__group-title">
            {g.label}
          </h2>
          <ul className="public-schedule__cards">
            {g.items.map((a, i) => {
              const prev = i > 0 ? g.items[i - 1] : null;
              const sameDayAsPrev = Boolean(prev) && sameDay(prev!.date, a.date, timeZone);
              return (
              <li
                key={`${a.date}-${i}`}
                className={`public-schedule__card${
                  sameDayAsPrev ? " public-schedule__card--continuation" : ""
                }`}
              >
                <div className="public-schedule__card-date">
                  {a.weekday}, {a.monthDay}
                </div>
                <div className="public-schedule__card-meta">
                  {a.time} Mass · {roleLabel(a.ministry)}
                  {a.position && a.position !== a.ministry
                    ? ` · ${roleLabel(a.position)}`
                    : ""}
                </div>
                <a
                  className="public-schedule__sub-link"
                  href={data.links.requestSubUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Request a sub&nbsp;→
                </a>
              </li>
              );
            })}
          </ul>
        </section>
      ))}
    </>
  );
}

function SkeletonList() {
  return (
    <div className="public-schedule__skeleton" aria-busy="true" aria-label="Loading">
      <div className="public-schedule__skeleton-line" />
      <div className="public-schedule__skeleton-line" />
      <div className="public-schedule__skeleton-line" />
    </div>
  );
}

function Notice({
  title,
  body,
  retry,
}: {
  title: string;
  body: string;
  retry?: () => void;
}) {
  return (
    <div className="public-schedule__notice">
      <p className="public-schedule__notice-title">{title}</p>
      <p className="public-schedule__notice-body">{body}</p>
      {retry ? (
        <button type="button" className="btn" onClick={retry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

interface MonthGroup {
  key: string; // "2026-05"
  label: string; // "May 2026"
  items: PublicAssignment[];
}

// Two assignments fall on the same parish-local calendar day. Used by
// the card renderer to mark cards as continuations of the previous
// day's first card, which tightens vertical spacing and signals
// the cluster visually.
function sameDay(a: string, b: string, timeZone: string): boolean {
  return zonedDayKey(new Date(a), timeZone) === zonedDayKey(new Date(b), timeZone);
}

function groupByMonth(assignments: PublicAssignment[], timeZone: string): MonthGroup[] {
  const map = new Map<string, MonthGroup>();
  for (const a of assignments) {
    const d = new Date(a.date);
    if (Number.isNaN(d.getTime())) continue;
    const key = zonedMonthKey(d, timeZone);
    let group = map.get(key);
    if (!group) {
      group = {
        key,
        label: d.toLocaleString("en-US", {
          month: "long",
          year: "numeric",
          timeZone,
        }),
        items: [],
      };
      map.set(key, group);
    }
    group.items.push(a);
  }
  return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
}
